import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { Add, UploadFile } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { DsButton } from '../design-system/components';
import apiClient from '../utils/apiClient';
import './ManagementList.css';

type Kind = 'orders' | 'flora-results' | 'stds-results' | 'enquetes';
type RecordItem = {
  id: number;
  order_id?: string;
  customer_id?: string;
  customer_email?: string;
  check_kit_code?: string | null;
  kit_code?: string;
  carte_no?: string;
  name?: string;
  check_id?: string;
  variant_id?: number;
  ecforce_order_id?: number | null;
  flora_check_result?: { id: number } | null;
  stds_check_result?: { id: number } | null;
  created_at?: string;
  [key: string]: unknown;
};
type PageResponse = { data: RecordItem[]; current_page: number; last_page: number; total: number };

const labels: Record<Kind, string> = {
  orders: '注文', 'flora-results': 'フローラ結果', 'stds-results': '性病検査結果', enquetes: '生活習慣アンケート',
};
const paths: Kind[] = ['orders', 'flora-results', 'stds-results', 'enquetes'];
const errorMessage = (error: unknown) => {
  const response = (error as AxiosError<{ message?: string; errors?: Record<string, string[]> }>).response;
  return (response?.data?.errors && Object.values(response.data.errors)[0]?.[0])
    || response?.data?.message || '処理に失敗しました。';
};
const date = (value?: string) => value ? new Date(value).toLocaleString('ja-JP') : '—';
const text = (value: unknown) => value === null || value === undefined || value === '' ? '—' : String(value);

const CheckKitManagementPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const requestedKind = new URLSearchParams(location.search).get('view');
  const kind: Kind = paths.includes(requestedKind as Kind) ? requestedKind as Kind : 'orders';
  const [rows, setRows] = useState<RecordItem[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [customerId, setCustomerId] = useState('');
  const [kitCode, setKitCode] = useState('');
  const [filters, setFilters] = useState({ customer_id: '', kit_code: '' });
  const [unassigned, setUnassigned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selected, setSelected] = useState<RecordItem | null>(null);
  const [detail, setDetail] = useState<RecordItem | null>(null);
  const [editKitCode, setEditKitCode] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [orderForm, setOrderForm] = useState({ order_id: '', customer_id: '', customer_email: '', variant_id: '' });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const response = await apiClient.get<PageResponse>(`/api/check-kits/${kind}`, {
        params: {
          page,
          ...(kind === 'orders' ? {
            customer_id: filters.customer_id || undefined,
            kit_code: filters.kit_code || undefined,
          } : {}),
          ...(kind === 'flora-results' && unassigned ? { unassigned: 1 } : {}),
        },
      });
      setRows(response.data.data);
      setLastPage(response.data.last_page || 1);
      setTotal(response.data.total || 0);
    } catch (err) { setError(errorMessage(err)); }
    finally { setLoading(false); }
  }, [kind, page, filters, unassigned]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); setRows([]); setError(''); setNotice(''); setUnassigned(false); }, [kind]);

  const changeKind = (next: Kind) => {
    navigate(`/check-kits?view=${next}`);
  };
  const openDetail = async (row: RecordItem) => {
    setError(''); setSelected(row); setDetail(null); setEditKitCode(row.check_kit_code || row.check_id || '');
    try {
      const response = await apiClient.get<RecordItem>(`/api/check-kits/${kind}/${row.id}`);
      setDetail(response.data);
    } catch (err) { setError(errorMessage(err)); setSelected(null); }
  };
  const saveKitCode = async () => {
    if (!selected || !editKitCode.trim()) return;
    setSaving(true); setError('');
    try {
      if (kind === 'orders') {
        await apiClient.put(`/api/check-kits/orders/${selected.id}/kit-code`, { check_kit_code: editKitCode.trim() });
      } else if (kind === 'enquetes') {
        await apiClient.patch(`/api/check-kits/enquetes/${selected.id}/kit-code`, { check_id: editKitCode.trim() });
      }
      setSelected(null); setNotice('キット ID を保存しました。'); await load();
    } catch (err) { setError(errorMessage(err)); }
    finally { setSaving(false); }
  };
  const createOrder = async () => {
    setSaving(true); setError('');
    try {
      await apiClient.post('/api/check-kits/orders', { ...orderForm, variant_id: Number(orderForm.variant_id) });
      setCreateOpen(false); setOrderForm({ order_id: '', customer_id: '', customer_email: '', variant_id: '' });
      setNotice('注文を登録しました。'); setPage(1); await load();
    } catch (err) { setError(errorMessage(err)); }
    finally { setSaving(false); }
  };
  const upload = async (file: File, target: 'flora-results' | 'stds-results') => {
    setSaving(true); setError(''); setNotice('');
    try {
      const data = new FormData(); data.append('csv', file);
      const response = await apiClient.post<{ imported: number; assigned?: number; failed?: number }>(`/api/check-kits/${target}/import`, data);
      setNotice(`${response.data.imported} 件を取り込みました。${response.data.assigned !== undefined ? ` うち注文に紐付いた件数: ${response.data.assigned} 件。` : ''}`);
      await load();
      if (response.data.failed) setError(`${response.data.failed} 件は取り込めませんでした。CSV を確認してください。`);
    } catch (err) { setError(errorMessage(err)); }
    finally { setSaving(false); }
  };
  const downloadImage = async (order: RecordItem) => {
    setError('');
    try {
      const response = await apiClient.get(`/api/check-kits/orders/${order.id}/application-image`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a'); link.href = url;
      link.download = `stds-application-${order.order_id || order.id}.png`; link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { setError(errorMessage(err)); }
  };

  return <Box className="management-page" sx={{ p: 0, minWidth: 0, maxWidth: 'none' }}>
    <div className="management-heading">
      <div><h1>検査キット管理</h1><p>注文、検査結果、生活習慣アンケートを確認します。</p></div>
      <div className="management-heading-actions">
        {kind === 'orders' && <DsButton type="button" tone="primary" icon={<Add />} onClick={() => setCreateOpen(true)}>注文を手動登録</DsButton>}
        {(kind === 'flora-results' || kind === 'stds-results') && <>
          <DsButton type="button" tone="primary" icon={<UploadFile />} disabled={saving} onClick={() => csvInputRef.current?.click()}>CSVアップロード</DsButton>
          <input ref={csvInputRef} hidden type="file" accept=".csv,text/csv" onChange={event => { const file = event.target.files?.[0]; if (file) void upload(file, kind); event.target.value = ''; }} />
        </>}
      </div>
    </div>
    {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
    {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Tabs value={kind} onChange={(_, value: Kind) => changeKind(value)} variant="scrollable" scrollButtons="auto" aria-label="検査キット管理の種類">
        {paths.map(path => <Tab key={path} value={path} label={labels[path]} />)}
      </Tabs>
    </Paper>
    {(kind === 'orders' || kind === 'flora-results') && <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        {kind === 'orders' && <>
          <TextField size="small" label="顧客 ID" value={customerId} onChange={event => setCustomerId(event.target.value)} />
          <TextField size="small" label="キット ID" value={kitCode} onChange={event => setKitCode(event.target.value)} />
          <Button variant="outlined" onClick={() => { setFilters({ customer_id: customerId.trim(), kit_code: kitCode.trim() }); setPage(1); }}>検索</Button>
        </>}
        {kind === 'flora-results' && <Button variant={unassigned ? 'contained' : 'outlined'} onClick={() => { setUnassigned(!unassigned); setPage(1); }}>未紐付けのみ</Button>}
      </Stack>
    </Paper>}
    <Paper variant="outlined">
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">{labels[kind]}一覧</Typography><Typography color="text.secondary">{total} 件</Typography>
      </Box>
      <TableContainer><Table size="small" aria-label={`${labels[kind]}一覧`}>
        <TableHead><TableRow>
          <TableCell>ID</TableCell><TableCell>{kind === 'orders' ? '注文 ID' : kind === 'flora-results' ? 'キット ID' : kind === 'stds-results' ? 'カルテ No.' : '氏名'}</TableCell>
          <TableCell>{kind === 'orders' ? '顧客 ID / メール' : kind === 'enquetes' ? '検査結果 ID' : '注文との紐付け'}</TableCell>
          <TableCell>{kind === 'orders' ? 'キット ID / 状態' : '登録日時'}</TableCell><TableCell>操作</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {loading && <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell></TableRow>}
          {!loading && rows.length === 0 && <TableRow><TableCell colSpan={5} align="center">データがありません</TableCell></TableRow>}
          {!loading && rows.map(row => <TableRow key={row.id} hover>
            <TableCell>{row.id}</TableCell>
            <TableCell>{text(kind === 'orders' ? row.order_id : kind === 'flora-results' ? row.kit_code : kind === 'stds-results' ? row.carte_no : row.name)}</TableCell>
            <TableCell>{kind === 'orders' ? <>{text(row.customer_id)}<br />{text(row.customer_email)}</> : kind === 'enquetes' ? text(row.check_id) : row.ecforce_order_id ? `注文 #${row.ecforce_order_id}` : <Chip size="small" color="warning" label="未紐付け" />}</TableCell>
            <TableCell>{kind === 'orders' ? <>{text(row.check_kit_code)}<br /><Chip size="small" label={row.flora_check_result || row.stds_check_result ? '結果あり' : '結果待ち'} /></> : date(row.created_at)}</TableCell>
            <TableCell><Button size="small" onClick={() => void openDetail(row)}>詳細</Button></TableCell>
          </TableRow>)}
        </TableBody>
      </Table></TableContainer>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" sx={{ p: 2 }}>
        <Button disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>前へ</Button>
        <Typography>{page} / {lastPage}</Typography>
        <Button disabled={page >= lastPage || loading} onClick={() => setPage(page + 1)}>次へ</Button>
      </Stack>
    </Paper>

    <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
      <DialogTitle>{labels[kind]}の詳細</DialogTitle>
      <DialogContent dividers>
        {detail ? <Stack spacing={1.5}>
          {Object.entries(detail).filter(([key]) => !['answers', 'flora_check_result', 'stds_check_result', 'ecforce_order'].includes(key)).map(([key, value]) =>
            <Box key={key}><Typography variant="caption" color="text.secondary">{key}</Typography><Typography sx={{ overflowWrap: 'anywhere' }}>{typeof value === 'object' && value !== null ? JSON.stringify(value) : text(value)}</Typography></Box>)}
          {(kind === 'orders' || kind === 'enquetes') && <TextField label={kind === 'orders' ? 'キット ID' : '検査結果 ID'} value={editKitCode} onChange={event => setEditKitCode(event.target.value)} fullWidth />}
        </Stack> : <CircularProgress />}
      </DialogContent>
      <DialogActions>
        {kind === 'orders' && detail && <Button onClick={() => void downloadImage(detail)}>性病検査申込書</Button>}
        {(kind === 'orders' || kind === 'enquetes') && <Button variant="contained" disabled={saving || !editKitCode.trim()} onClick={() => void saveKitCode()}>ID を保存</Button>}
        <Button onClick={() => setSelected(null)}>閉じる</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>注文を手動登録</DialogTitle>
      <DialogContent dividers><Stack spacing={2} sx={{ pt: 1 }}>
        {([['order_id', '注文 ID'], ['customer_id', '顧客 ID'], ['customer_email', 'メールアドレス'], ['variant_id', '商品 variant ID']] as const).map(([key, label]) =>
          <TextField key={key} label={label} required value={orderForm[key]} onChange={event => setOrderForm({ ...orderForm, [key]: event.target.value })} type={key === 'customer_email' ? 'email' : 'text'} />)}
      </Stack></DialogContent>
      <DialogActions><Button onClick={() => setCreateOpen(false)}>キャンセル</Button><Button variant="contained" disabled={saving || Object.values(orderForm).some(value => !value.trim()) || !Number.isInteger(Number(orderForm.variant_id)) || Number(orderForm.variant_id) <= 0} onClick={() => void createOrder()}>登録</Button></DialogActions>
    </Dialog>
  </Box>;
};

export default CheckKitManagementPage;
